import React, { useState, useEffect } from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line
} from 'recharts';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import { getIconComponent } from '../utils/iconHelper';
import { MdCalendarToday, MdCategory, MdFilterList, MdExpandMore, MdExpandLess } from 'react-icons/md';
import './Reports.css';

function Reports() {
  const [activeTab, setActiveTab] = useState('expenses');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 180), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Dados para abas de despesas e receitas
  const [transactions, setTransactions] = useState([]);
  const [categoryTotals, setCategoryTotals] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  
  // Dados para aba mês a mês
  const [monthlyData, setMonthlyData] = useState([]);
  
  // Dados para aba de evolução por categoria
  const [selectedCategoryForEvolution, setSelectedCategoryForEvolution] = useState(null);
  const [categoryEvolutionData, setCategoryEvolutionData] = useState([]);
  const [selectedCategoryModel, setSelectedCategoryModel] = useState(null);
  const [categoryEvolutionTransactions, setCategoryEvolutionTransactions] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  
  // PDF Report states
  const [pdfFilters, setPdfFilters] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'all',
    includeVirtual: true,
    categoryIds: []
  });
  
  // PDF Viewer states
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    // Ao entrar na aba de Evolução, configurar últimos 5 meses
    if (activeTab === 'evolution') {
      const now = new Date();
      const fiveMonthsAgo = subMonths(now, 4); // 4 meses atrás + mês atual = 5 meses
      setStartDate(format(startOfMonth(fiveMonthsAgo), 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
    }
  }, [activeTab]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, activeTab, selectedCategoryIds, selectedCategoryForEvolution]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    
    try {
      console.log('📊 Relatórios - Aplicando filtros:', {
        periodo: { startDate, endDate },
        abaAtiva: activeTab,
        categoriasSelecionadas: selectedCategoryIds,
        categoriaEvolucao: selectedCategoryForEvolution
      });

      // Para abas de despesas e receitas - usar novo endpoint
      if (activeTab === 'expenses' || activeTab === 'income') {
        const type = activeTab === 'expenses' ? 'expense' : 'income';
        
        const response = await transactionService.getCategoryDistributionReport(
          type,
          startDate,
          endDate,
          selectedCategoryIds
        );
        
        if (response.success && response.data) {
          const data = response.data;
          
          // Preparar totais por categoria
          const totals = {};
          const allTransactions = [];
          
          data.categories.forEach(cat => {
            totals[cat.category_id] = cat.amount;
          });
          
          // Buscar transações detalhadas (ainda usando o endpoint antigo temporariamente)
          const transResponse = await transactionService.getTransactions({
            start_date: startDate,
            end_date: endDate,
            type: type,
            per_page: 1000,
          });
          
          const filteredTransactions = Array.isArray(transResponse?.data) 
            ? transResponse.data.filter(t => {
                if (selectedCategoryIds.length === 0) return true;
                return selectedCategoryIds.includes(t.category_id);
              })
            : [];
          
          setTransactions(filteredTransactions);
          setCategoryTotals(totals);
          setTotalAmount(data.total);
        }
      }

      // Para aba mês a mês - usar novo endpoint
      if (activeTab === 'monthly') {
        const response = await transactionService.getMonthlyAggregatedReport(
          startDate,
          endDate,
          selectedCategoryIds
        );
        
        if (response.success && response.data) {
          console.log('📊 Mês a Mês - Dados do servidor:', response.data);
          setMonthlyData(response.data);
        }
      }

      // Para aba de evolução por categoria - usar novos endpoints
      if (activeTab === 'evolution' && selectedCategoryForEvolution) {
        // Buscar evolução agregada
        const evolutionResponse = await transactionService.getCategoryEvolutionReport(
          selectedCategoryForEvolution,
          startDate,
          endDate
        );
        
        if (evolutionResponse.success && evolutionResponse.data) {
          console.log('📈 Evolução - Dados do servidor:', evolutionResponse.data);
          
          const { category, evolution } = evolutionResponse.data;
          
          // Preparar dados para o gráfico
          const chartData = evolution.map(item => ({
            month: item.month,
            amount: item.amount
          }));
          
          // Buscar transações detalhadas para cada mês
          const transactionsByMonth = {};
          for (const item of evolution) {
            try {
              const monthTransResponse = await transactionService.getCategoryMonthTransactions(
                selectedCategoryForEvolution,
                item.month
              );
              
              if (monthTransResponse.success && monthTransResponse.data) {
                transactionsByMonth[item.month] = monthTransResponse.data;
              }
            } catch (error) {
              console.error(`Erro ao buscar transações do mês ${item.month}:`, error);
              transactionsByMonth[item.month] = [];
            }
          }
          
          setCategoryEvolutionData(chartData);
          setCategoryEvolutionTransactions(transactionsByMonth);
          setSelectedCategoryModel(category);
        }
      } else if (activeTab === 'evolution') {
        setCategoryEvolutionData([]);
        setCategoryEvolutionTransactions({});
        setSelectedCategoryModel(null);
      }

    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const result = await transactionService.generatePdfReport(pdfFilters, true); // true = visualizar
      setPdfUrl(result.url);
      setShowPdfViewer(true);
      console.log('✅ PDF gerado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      
      let errorMessage = 'Erro ao gerar PDF. Tente novamente.';
      
      // Extrair mensagem de erro do backend
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join('\n');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `relatorio-transacoes-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCompactCurrency = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}k`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      // Aceitar formato YYYY-MM-DD ou timestamps
      const date = dateString.includes('T') 
        ? new Date(dateString)
        : new Date(dateString + 'T00:00:00');
      if (isNaN(date.getTime())) return dateString;
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return dateString || '-';
    }
  };

  const formatMonth = (monthKey) => {
    try {
      if (!monthKey) return '-';
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      if (isNaN(date.getTime())) return monthKey;
      return format(date, 'MMM/yy', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar mês:', monthKey, error);
      return monthKey || '-';
    }
  };

  const formatMonthFull = (monthKey) => {
    try {
      if (!monthKey) return '-';
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      if (isNaN(date.getTime())) return monthKey;
      return format(date, 'MMMM/yyyy', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar mês completo:', monthKey, error);
      return monthKey || '-';
    }
  };

  // Cores para gráficos
  const EXPENSE_COLORS = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#ec4899',
    '#f472b6', '#ff6b6b', '#ff5252', '#dc2626', '#fb923c',
    '#fbbf24', '#a16207', '#facc15'
  ];

  const INCOME_COLORS = [
    '#10b981', '#14b8a6', '#06b6d4', '#22c55e', '#34d399',
    '#2dd4bf', '#5eead4', '#059669', '#0d9488', '#0891b2',
    '#16a34a', '#10b981', '#14b8a6'
  ];

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Desconhecida';
  };

  const getCategory = (categoryId) => {
    return categories.find(c => c.id === categoryId);
  };

  // Preparar dados para gráfico de pizza
  const preparePieChartData = () => {
    const colors = activeTab === 'expenses' ? EXPENSE_COLORS : INCOME_COLORS;
    return Object.keys(categoryTotals).map((categoryId, index) => ({
      name: getCategoryName(categoryId),
      value: categoryTotals[categoryId],
      categoryId: categoryId,
      color: colors[index % colors.length],
      percentage: ((categoryTotals[categoryId] / totalAmount) * 100).toFixed(1),
    }));
  };

  // Renderizar aba de despesas/receitas
  const renderExpenseIncomeTab = () => {
    const pieData = preparePieChartData();
    const color = activeTab === 'expenses' ? '#ef4444' : '#10b981';

    return (
      <div className="report-content">
        {/* Card de resumo */}
        <div className="report-card">
          <h3>Resumo</h3>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">Total</span>
              <span className="summary-value" style={{ color }}>
                {formatCurrency(totalAmount)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Quantidade</span>
              <span className="summary-value">
                {transactions.length} transações
              </span>
            </div>
          </div>
        </div>

        {/* Gráfico de pizza */}
        {pieData.length > 0 ? (
          <div className="report-card">
            <h3>Distribuição por Categoria</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percentage }) => `${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Legenda customizada */}
            <div className="chart-legend">
              {pieData.map((entry, index) => (
                <div key={index} className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="legend-name">{entry.name}</span>
                  <span className="legend-percentage">{entry.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p>Sem dados para exibir</p>
          </div>
        )}

        {/* Lista de transações */}
        {transactions.length > 0 && (
          <div className="report-card">
            <h3>Transações</h3>
            <div className="transactions-list">
              {transactions.filter(t => t && t.id && t.date).map((transaction) => {
                const category = getCategory(transaction.category_id);
                const CategoryIcon = category ? getIconComponent(category.icon) : null;

                // Validar data antes de renderizar
                let displayDate = '-';
                try {
                  if (transaction.date) {
                    const testDate = new Date(transaction.date + 'T00:00:00');
                    if (!isNaN(testDate.getTime())) {
                      displayDate = formatDate(transaction.date);
                    }
                  }
                } catch (error) {
                  console.error('Erro ao processar data da transação:', transaction.id, transaction.date, error);
                }

                return (
                  <div key={transaction.id} className="transaction-item-report">
                    <div
                      className="transaction-icon-small"
                      style={{ backgroundColor: category?.color || '#999' }}
                    >
                      {CategoryIcon && <CategoryIcon size={20} color="white" />}
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-title">{transaction.description || 'Sem descrição'}</div>
                      <div className="transaction-subtitle">
                        {getCategoryName(transaction.category_id)} • {displayDate}
                      </div>
                    </div>
                    <div className="transaction-amount" style={{ color }}>
                      {formatCurrency(transaction.amount || 0)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renderizar aba mês a mês
  const renderMonthlyTab = () => {
    console.log('🎨 Renderizando aba Mês a Mês:', {
      monthlyDataLength: monthlyData.length,
      monthlyData: monthlyData
    });
    
    // Limitar aos últimos 12 meses para melhor visualização
    const displayData = monthlyData.slice(-12);

    return (
      <div className="report-content">
        <div className="report-card">
          <h3>Resultado Mensal</h3>
          
          {displayData.length > 0 ? (
            <>
              {/* Gráfico de barras */}
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={displayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonth}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={formatCompactCurrency}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={formatMonthFull}
                  />
                  <Legend />
                  <Bar dataKey="income" fill="#10b981" name="Receitas" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" fill="#ef4444" name="Despesas" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="balance" fill="#E3A864" name="Saldo" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Tabela de detalhamento */}
              <div className="monthly-table">
                <h4>Detalhamento Mensal</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Mês</th>
                      <th className="text-right">Receitas</th>
                      <th className="text-right">Despesas</th>
                      <th className="text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((row) => (
                      <tr key={row.month}>
                        <td className="font-bold">{formatMonth(row.month)}</td>
                        <td className="text-right text-green">{formatCompactCurrency(row.income)}</td>
                        <td className="text-right text-red">{formatCompactCurrency(row.expense)}</td>
                        <td className={`text-right font-bold ${row.balance >= 0 ? 'text-blue' : 'text-red'}`}>
                          {formatCompactCurrency(row.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Nenhum dado encontrado para o período selecionado</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizar aba de evolução por categoria
  const renderEvolutionTab = () => {
    console.log('🎨 Renderizando aba Evolução:', {
      selectedCategoryForEvolution,
      categoryEvolutionDataLength: categoryEvolutionData.length,
      categoryEvolutionData: categoryEvolutionData,
      selectedCategoryModel
    });
    
    if (!selectedCategoryForEvolution) {
      return (
        <div className="empty-state-full">
          <MdCategory size={64} color="#ccc" />
          <h3>Selecione uma categoria para visualizar sua evolução</h3>
          <p>Período sugerido: últimos 5 meses</p>
          <button
            className="btn-primary"
            onClick={() => setShowCategorySelector(true)}
          >
            <MdCategory size={20} />
            Selecionar Categoria
          </button>
        </div>
      );
    }

    if (categoryEvolutionData.length === 0) {
      return (
        <div className="empty-state-full">
          <MdCategory size={64} color="#ccc" />
          <h3>Nenhuma transação encontrada para esta categoria</h3>
          <p>Tente ajustar os filtros de data para ver mais resultados</p>
          <button
            className="btn-primary"
            onClick={() => setShowCategorySelector(true)}
          >
            <MdCategory size={20} />
            Trocar Categoria
          </button>
        </div>
      );
    }

    const color = selectedCategoryModel?.type === 'expense' ? '#ef4444' : '#10b981';
    const totalPeriod = categoryEvolutionData.reduce((sum, item) => sum + item.amount, 0);
    const averageMonthly = totalPeriod / categoryEvolutionData.length;
    const CategoryIcon = selectedCategoryModel ? getIconComponent(selectedCategoryModel.icon) : null;

    const toggleMonthExpansion = (monthKey) => {
      setExpandedMonths(prev => ({
        ...prev,
        [monthKey]: !prev[monthKey]
      }));
    };

    return (
      <div className="report-content">
        {/* Card de informações da categoria */}
        <div className="report-card">
          <div className="category-header">
            <div className="category-info">
              <div
                className="category-icon-large"
                style={{ backgroundColor: selectedCategoryModel?.color || color }}
              >
                {CategoryIcon && <CategoryIcon size={32} color="white" />}
              </div>
              <div>
                <h3>{selectedCategoryModel?.name}</h3>
                <span className="category-type" style={{ color }}>
                  {selectedCategoryModel?.type === 'expense' ? 'Despesa' : 'Receita'}
                </span>
              </div>
            </div>
            <button
              className="btn-secondary"
              onClick={() => setShowCategorySelector(true)}
            >
              Trocar
            </button>
          </div>
          
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">Total no período</span>
              <span className="summary-value" style={{ color }}>
                {formatCurrency(totalPeriod)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Média mensal</span>
              <span className="summary-value" style={{ color }}>
                {formatCurrency(averageMonthly)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Meses analisados</span>
              <span className="summary-value">
                {categoryEvolutionData.length}
              </span>
            </div>
          </div>
        </div>

        {/* Gráfico de linha */}
        <div className="report-card">
          <h3>Evolução Mensal</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={categoryEvolutionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatCompactCurrency}
                tick={{ fontSize: 10 }}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={formatMonthFull}
              />
              <Line
                type="monotone"
                dataKey="amount"
                stroke={color}
                strokeWidth={3}
                dot={{ fill: color, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Tabela de detalhamento com transações */}
        <div className="report-card">
          <h4>Detalhamento Mensal - Transações</h4>
          <div className="evolution-table">
            {categoryEvolutionData.map((item) => {
              const monthTransactions = categoryEvolutionTransactions[item.month] || [];
              const isExpanded = expandedMonths[item.month];
              
              return (
                <div key={item.month} className="evolution-month-group">
                  <div 
                    className="evolution-month-header"
                    onClick={() => toggleMonthExpansion(item.month)}
                  >
                    <div className="evolution-month-info">
                      {isExpanded ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
                      <div>
                        <span className="evolution-month-name">{formatMonthFull(item.month)}</span>
                        <span className="evolution-month-count">
                          {monthTransactions.length} {monthTransactions.length === 1 ? 'transação' : 'transações'}
                        </span>
                      </div>
                    </div>
                    <span className="evolution-month-total" style={{ color }}>
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                  
                  {isExpanded && (
                    <div className="evolution-month-transactions">
                      {monthTransactions.length > 0 ? (
                        monthTransactions
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((transaction) => {
                            const category = getCategory(transaction.category_id);
                            const TransactionIcon = category ? getIconComponent(category.icon) : null;
                            
                            return (
                              <div key={transaction.id} className="evolution-transaction-item">
                                <div
                                  className="transaction-icon-small"
                                  style={{ backgroundColor: category?.color || '#999' }}
                                >
                                  {TransactionIcon && <TransactionIcon size={20} color="white" />}
                                </div>
                                <div className="transaction-details">
                                  <div className="transaction-title">
                                    {transaction.description || 'Sem descrição'}
                                  </div>
                                  <div className="transaction-subtitle">
                                    {formatDate(transaction.date)}
                                    {transaction.account_name && ` • ${transaction.account_name}`}
                                  </div>
                                </div>
                                <div className="transaction-amount" style={{ color }}>
                                  {formatCurrency(transaction.amount || 0)}
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="empty-state-small">
                          <p>Nenhuma transação neste mês</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar aba de geração de PDF
  const renderPdfTab = () => {
    const expenseCats = categories.filter(c => c.type === 'expense');
    const incomeCats = categories.filter(c => c.type === 'income');
    const allCats = [...expenseCats, ...incomeCats];

    return (
      <div className="report-content">
        {/* Card de configuração do PDF */}
        <div className="report-card">
          <h3>📄 Configuração do Relatório PDF</h3>
          <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
            Configure os filtros abaixo e clique em "Gerar PDF" para baixar o relatório completo com todas as transações.
          </p>

          <div className="pdf-config-form">
            {/* Período */}
            <div className="form-section">
              <label>
                <MdCalendarToday size={16} />
                Período
              </label>
              <div className="date-range">
                <input
                  type="date"
                  value={pdfFilters.startDate}
                  onChange={(e) => setPdfFilters({ ...pdfFilters, startDate: e.target.value })}
                />
                <span>até</span>
                <input
                  type="date"
                  value={pdfFilters.endDate}
                  onChange={(e) => setPdfFilters({ ...pdfFilters, endDate: e.target.value })}
                />
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <label>
                <MdFilterList size={16} />
                Situação das Transações
              </label>
              <select
                value={pdfFilters.status}
                onChange={(e) => setPdfFilters({ ...pdfFilters, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white',
                  transition: 'all 0.3s'
                }}
              >
                <option value="all">Todas (Liquidadas e Pendentes)</option>
                <option value="paid">Apenas Liquidadas</option>
                <option value="pending">Apenas Pendentes</option>
              </select>
            </div>

            {/* Incluir Virtuais */}
            <div className="form-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={pdfFilters.includeVirtual}
                  onChange={(e) => setPdfFilters({ ...pdfFilters, includeVirtual: e.target.checked })}
                  style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--secondary-color)' }}
                />
                Incluir Transações Virtuais (não efetivadas)
              </label>
            </div>

            {/* Categorias */}
            <div className="form-section">
              <label>
                <MdCategory size={16} />
                Filtrar por Categorias
              </label>
              <button
                className="btn-filter"
                onClick={() => setShowFilterDialog(true)}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <MdFilterList size={18} />
                {pdfFilters.categoryIds.length === 0
                  ? 'Todas as Categorias'
                  : `${pdfFilters.categoryIds.length} categoria(s) selecionada(s)`}
              </button>
              {pdfFilters.categoryIds.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {pdfFilters.categoryIds.map(catId => {
                    const cat = allCats.find(c => c.id === catId);
                    const CategoryIcon = cat ? getIconComponent(cat.icon) : null;
                    return (
                      <div
                        key={catId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'white',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            background: cat?.color || '#999',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {CategoryIcon && <CategoryIcon size={12} color="white" />}
                        </div>
                        {cat?.name || 'Desconhecida'}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Botão Gerar PDF */}
            <button
              className="btn-primary"
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                marginTop: '20px',
                opacity: generatingPdf ? 0.6 : 1,
                cursor: generatingPdf ? 'not-allowed' : 'pointer'
              }}
            >
              {generatingPdf ? (
                <>
                  <div className="spinner" style={{ width: '20px', height: '20px', marginRight: '10px' }}></div>
                  Gerando PDF...
                </>
              ) : (
                <>
                  📄 Gerar Relatório PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Card de informações */}
        <div className="report-card">
          <h3>ℹ️ Sobre o Relatório PDF</h3>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
            <p style={{ marginBottom: '12px' }}>
              <strong>O que está incluído no relatório:</strong>
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
              <li>Resumo financeiro completo do período</li>
              <li>Totais de receitas e despesas</li>
              <li>Saldos (liquidado e pendente)</li>
              <li>Lista detalhada de todas as transações</li>
              <li>Informações de categoria e conta de cada transação</li>
              <li>Indicadores visuais de status (pago/pendente/virtual)</li>
            </ul>
            <p style={{ marginBottom: '12px' }}>
              <strong>Dicas:</strong>
            </p>
            <ul style={{ marginLeft: '20px' }}>
              <li>Use filtros para gerar relatórios específicos</li>
              <li>O PDF é gerado em formato A4 para impressão</li>
              <li>Transações virtuais são marcadas claramente</li>
              <li>Perfeito para controle mensal ou anual</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const allCategories = [...expenseCategories, ...incomeCategories];

  return (
    <div className="reports">
      <div className="reports-header">
        <h1>Relatórios</h1>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('expenses');
            setSelectedCategoryIds([]);
          }}
        >
          Despesas
        </button>
        <button
          className={`tab ${activeTab === 'income' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('income');
            setSelectedCategoryIds([]);
          }}
        >
          Receitas
        </button>
        <button
          className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('monthly');
            setSelectedCategoryIds([]);
          }}
        >
          Mês a Mês
        </button>
        <button
          className={`tab ${activeTab === 'evolution' ? 'active' : ''}`}
          onClick={() => setActiveTab('evolution')}
        >
          Evolução
        </button>
        <button
          className={`tab ${activeTab === 'pdf' ? 'active' : ''}`}
          onClick={() => setActiveTab('pdf')}
        >
          📄 Gerar PDF
        </button>
      </div>

      {/* Filtros - Ocultar na aba PDF */}
      {activeTab !== 'pdf' && (
        <div className="filters">
          <h3>Filtros</h3>
          <div className="filter-row">
          <div className="filter-group">
            <label>
              <MdCalendarToday size={16} />
              Período
            </label>
            <div className="date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>até</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            
            {activeTab === 'evolution' && (
              <div className="period-shortcuts">
                <button
                  className="btn-shortcut"
                  onClick={() => {
                    const now = new Date();
                    const threeMonthsAgo = subMonths(now, 2);
                    setStartDate(format(startOfMonth(threeMonthsAgo), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                  }}
                >
                  Últimos 3 meses
                </button>
                <button
                  className="btn-shortcut active"
                  onClick={() => {
                    const now = new Date();
                    const fiveMonthsAgo = subMonths(now, 4);
                    setStartDate(format(startOfMonth(fiveMonthsAgo), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                  }}
                >
                  Últimos 5 meses
                </button>
                <button
                  className="btn-shortcut"
                  onClick={() => {
                    const now = new Date();
                    const yearAgo = subMonths(now, 11);
                    setStartDate(format(startOfMonth(yearAgo), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                  }}
                >
                  Últimos 12 meses
                </button>
              </div>
            )}
          </div>
          
          {activeTab !== 'evolution' && (
            <button
              className="btn-filter"
              onClick={() => setShowFilterDialog(true)}
            >
              <MdFilterList size={18} />
              Categorias
            </button>
          )}
        </div>
      </div>
      )}

      {/* Conteúdo */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      ) : (
        <>
          {(activeTab === 'expenses' || activeTab === 'income') && renderExpenseIncomeTab()}
          {activeTab === 'monthly' && renderMonthlyTab()}
          {activeTab === 'evolution' && renderEvolutionTab()}
          {activeTab === 'pdf' && renderPdfTab()}
        </>
      )}

      {/* Dialog de filtro de categorias */}
      {showFilterDialog && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Filtrar por Categorias</h2>
              <button className="close-btn" onClick={() => setShowFilterDialog(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="category-filter-list">
                {(() => {
                  const currentCategories = activeTab === 'pdf' ? allCategories :
                    activeTab === 'monthly' ? allCategories :
                    activeTab === 'expenses' ? expenseCategories : incomeCategories;
                  const allCategoryIds = currentCategories.map(c => c.id);
                  const currentSelectedIds = activeTab === 'pdf' ? pdfFilters.categoryIds : selectedCategoryIds;
                  const allSelected = currentSelectedIds.length === 0;
                  
                  return (
                    <>
                      <label className="category-filter-item">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => {
                            if (activeTab === 'pdf') {
                              if (e.target.checked) {
                                setPdfFilters({ ...pdfFilters, categoryIds: [] });
                              } else {
                                if (currentCategories.length > 0) {
                                  setPdfFilters({ ...pdfFilters, categoryIds: [currentCategories[0].id] });
                                }
                              }
                            } else {
                              if (e.target.checked) {
                                setSelectedCategoryIds([]);
                              } else {
                                if (currentCategories.length > 0) {
                                  setSelectedCategoryIds([currentCategories[0].id]);
                                }
                              }
                            }
                          }}
                        />
                        <span>Todas as categorias</span>
                      </label>
                      
                      <div className="divider" />
                      
                      {currentCategories.map((category) => {
                        const CategoryIcon = getIconComponent(category.icon);
                        const isChecked = currentSelectedIds.length === 0 || 
                          currentSelectedIds.includes(category.id);
                        
                        return (
                          <label key={category.id} className="category-filter-item">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (activeTab === 'pdf') {
                                  if (pdfFilters.categoryIds.length === 0) {
                                    setPdfFilters({ ...pdfFilters, categoryIds: [category.id] });
                                  } else if (pdfFilters.categoryIds.includes(category.id)) {
                                    const newSelection = pdfFilters.categoryIds.filter(id => id !== category.id);
                                    if (newSelection.length === 0) {
                                      setPdfFilters({ ...pdfFilters, categoryIds: [] });
                                    } else {
                                      setPdfFilters({ ...pdfFilters, categoryIds: newSelection });
                                    }
                                  } else {
                                    const newSelection = [...pdfFilters.categoryIds, category.id];
                                    if (newSelection.length === allCategoryIds.length) {
                                      setPdfFilters({ ...pdfFilters, categoryIds: [] });
                                    } else {
                                      setPdfFilters({ ...pdfFilters, categoryIds: newSelection });
                                    }
                                  }
                                } else {
                                  if (selectedCategoryIds.length === 0) {
                                    setSelectedCategoryIds([category.id]);
                                  } else if (selectedCategoryIds.includes(category.id)) {
                                    const newSelection = selectedCategoryIds.filter(id => id !== category.id);
                                    if (newSelection.length === 0) {
                                      setSelectedCategoryIds([]);
                                    } else {
                                      setSelectedCategoryIds(newSelection);
                                    }
                                  } else {
                                    const newSelection = [...selectedCategoryIds, category.id];
                                    if (newSelection.length === allCategoryIds.length) {
                                      setSelectedCategoryIds([]);
                                    } else {
                                      setSelectedCategoryIds(newSelection);
                                    }
                                  }
                                }
                              }}
                            />
                            <div
                              className="category-icon-filter"
                              style={{ backgroundColor: category.color }}
                            >
                              {CategoryIcon && <CategoryIcon size={16} color="white" />}
                            </div>
                            <span>{category.name}</span>
                          </label>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowFilterDialog(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowFilterDialog(false);
                  applyFilters();
                }}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog de seleção de categoria para evolução */}
      {showCategorySelector && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Selecionar Categoria</h2>
              <button className="close-btn" onClick={() => setShowCategorySelector(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="category-selector-list">
                <h4 className="category-section-title text-red">Despesas</h4>
                {expenseCategories.map((category) => {
                  const CategoryIcon = getIconComponent(category.icon);
                  return (
                    <button
                      key={category.id}
                      className="category-selector-item"
                      onClick={() => {
                        setSelectedCategoryForEvolution(category.id);
                        setShowCategorySelector(false);
                      }}
                    >
                      <div
                        className="category-icon-filter"
                        style={{ backgroundColor: category.color }}
                      >
                        {CategoryIcon && <CategoryIcon size={20} color="white" />}
                      </div>
                      <span>{category.name}</span>
                    </button>
                  );
                })}

                <h4 className="category-section-title text-green">Receitas</h4>
                {incomeCategories.map((category) => {
                  const CategoryIcon = getIconComponent(category.icon);
                  return (
                    <button
                      key={category.id}
                      className="category-selector-item"
                      onClick={() => {
                        setSelectedCategoryForEvolution(category.id);
                        setShowCategorySelector(false);
                      }}
                    >
                      <div
                        className="category-icon-filter"
                        style={{ backgroundColor: category.color }}
                      >
                        {CategoryIcon && <CategoryIcon size={20} color="white" />}
                      </div>
                      <span>{category.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowCategorySelector(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal do Visualizador de PDF */}
      {showPdfViewer && pdfUrl && (
        <div className="modal pdf-viewer-modal">
          <div className="modal-content pdf-viewer-content">
            <div className="modal-header">
              <h2>📄 Visualizar Relatório PDF</h2>
              <button className="close-btn" onClick={handleClosePdfViewer}>
                ✕
              </button>
            </div>
            <div className="modal-body pdf-viewer-body">
              <iframe
                src={pdfUrl}
                title="Visualizador de PDF"
                className="pdf-iframe"
              />
            </div>
            <div className="modal-footer pdf-viewer-footer">
              <button
                className="btn-secondary"
                onClick={handleClosePdfViewer}
              >
                Fechar
              </button>
              <button
                className="btn-primary"
                onClick={handleDownloadPdf}
              >
                📥 Baixar PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;
